/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * @module bulb-progress/bulb-transcoding/bulbtranscodingediting
 */

import { Plugin, Widget, toWidget } from 'ckeditor5';

import { InsertBulbTranscoderCommand } from './bulbtranscodingcommand';

/**
 * The bulb attached file editing, this will create the block using the converters.
 *
 * @extends Plugin
 */
export default class BulbTranscodingEditing extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [Widget] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbTranscodingEditing' as const;
  }

  public init(): void {
    this.defineSchema();
    this.defineConverters();

    this.editor.commands.add('insertBulbTranscoder', new InsertBulbTranscoderCommand(this.editor));
  }

  private defineSchema(): void {
    const { schema } = this.editor.model;

    schema.register('bulbTranscoder', {
      isObject: true,
      allowWhere: '$block',
      allowAttributes: ['bulb-block-id'],
    });
  }

  private defineConverters(): void {
    const { conversion } = this.editor;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'transcoder',
      },
      model: (viewElement, { writer: modelWriter }) => modelWriter.createElement('bulbTranscoder', {
        'bulb-block-id': viewElement.getAttribute('bulb-block-id'),
      }),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'bulbTranscoder',
      view: (modelElement, { writer: viewWriter }) => viewWriter.createEmptyElement('figure', {
        class: 'transcoder',
        'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
      }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'bulbTranscoder',
      view: (modelElement, { writer: viewWriter }) => {
        const emptyElement = viewWriter.createEmptyElement('img');
        const figure = viewWriter.createContainerElement('figure', {
          class: 'transcoder',
          'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
        });

        viewWriter.insert(viewWriter.createPositionAt(figure, 0), emptyElement);
        viewWriter.setCustomProperty('transcoder', true, figure);

        return toWidget(figure, viewWriter, { label: 'Bulb Transcoder', hasSelectionHandle: true });
      },
    });
  }
}
